import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { centralizedSubscriptionService } from '@/lib/mailbox-subscription-service';
import { useQueryClient } from '@tanstack/react-query';

interface Contact {
  id: string;
  account_id: string;
  client_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  linkedin_url?: string;
  linkedin_profile_url?: string;
  seniority_level?: string;
  is_primary_contact: boolean;
  contact_status: string;
  last_contacted_date?: string;
  last_activity_date?: string;
  notes?: string;
  engagement_status?: string;
  engagement_date?: string;
  contact_type?: string;
  decision_maker?: boolean;
  good_fit_for_reviews?: boolean;
  good_fit_for_loyalty?: boolean;
  good_fit_for_email_sms?: boolean;
  good_fit_for_subscription?: boolean;
  annual_revenue_estimate?: number;
  company_size_estimate?: string;
  profile_image_url?: string;
  linkedin_profile_image_url?: string;
  employment_status?: string;
  created_at: string;
  updated_at: string;
}

interface NewContact {
  company_id?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  primary_email?: string;
  primary_phone?: string;
  primary_linkedin?: string;
  title?: string;
  department?: string;
  seniority?: string;
  source?: string;
  // Legacy field names accepted for backward compatibility
  account_id?: string;
  client_id?: number;
  email?: string;
  phone?: string;
  job_title?: string;
  linkedin_url?: string;
  seniority_level?: string;
}

export const useContacts = (companyId?: string, clientId?: number | string, page?: number, pageSize?: number, search?: string) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const limit = pageSize || 200;
      const offset = ((page || 1) - 1) * limit;

      // If clientId is provided, use the optimized RPC that filters by TAM membership
      if (clientId && clientId !== 'all' && !companyId) {
        const clientIdStr = String(clientId);
        const { data: rpcResult, error: rpcError } = await supabase.rpc('get_client_contacts', {
          p_client_id: clientIdStr,
          p_limit: limit,
          p_offset: offset,
          p_search: search || null,
        });

        if (rpcError) throw rpcError;

        const total = rpcResult?.total || 0;
        const rows = rpcResult?.contacts || [];

        setTotalCount(total);

        const mapped = rows.map((row: any) => ({
          ...row,
          email: row.primary_email || row.email || '',
          phone: row.primary_phone || row.phone || null,
          job_title: row.title || row.job_title || null,
          linkedin_url: row.primary_linkedin || row.linkedin_url || null,
          seniority_level: row.seniority || row.seniority_level || null,
          account_id: row.company_id || row.account_id || '',
          client_id: clientId || row.client_id || 0,
        }));

        setContacts(mapped);
        setError(null);
        return;
      }

      // Default: fetch all contacts (no client filter)
      let query = supabase
        .from('contacts')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      if (search) {
        query = query.or(`full_name.ilike.%${search}%,primary_email.ilike.%${search}%,first_name.ilike.%${search}%,last_name.ilike.%${search}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setTotalCount(count || 0);

      // Map new DB schema columns to legacy interface names for backward compatibility
      const mapped = (data || []).map((row: any) => ({
        ...row,
        // Map new column names → legacy names used by downstream components
        email: row.primary_email || row.email || '',
        phone: row.primary_phone || row.phone || null,
        job_title: row.title || row.job_title || null,
        linkedin_url: row.primary_linkedin || row.linkedin_url || null,
        seniority_level: row.seniority || row.seniority_level || null,
        account_id: row.company_id || row.account_id || '',
        client_id: clientId || row.client_id || 0,
      }));

      setContacts(mapped);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createContact = async (contactData: NewContact) => {
    try {
      // Map legacy field names to new schema before insert
      const insertData: Record<string, any> = {
        company_id: contactData.company_id || contactData.account_id || null,
        first_name: contactData.first_name || null,
        last_name: contactData.last_name || null,
        full_name: contactData.full_name || (contactData.first_name && contactData.last_name ? `${contactData.first_name} ${contactData.last_name}` : null),
        primary_email: contactData.primary_email || contactData.email || null,
        primary_phone: contactData.primary_phone || contactData.phone || null,
        primary_linkedin: contactData.primary_linkedin || contactData.linkedin_url || null,
        title: contactData.title || contactData.job_title || null,
        department: contactData.department || null,
        seniority: contactData.seniority || contactData.seniority_level || null,
        source: contactData.source || 'manual',
      };

      const { data, error } = await supabase
        .from('contacts')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Contact created",
        description: `${contactData.first_name} ${contactData.last_name} has been added.`,
      });

      await fetchContacts();
      return data;
    } catch (err) {
      console.error('Error creating contact:', err);
      toast({
        title: "Error",
        description: "Failed to create contact. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateContact = async (id: string, updates: Partial<NewContact>) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Contact updated",
        description: "Contact information has been updated successfully.",
      });

      await fetchContacts();
    } catch (err) {
      console.error('Error updating contact:', err);
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteContact = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Contact deleted",
        description: "Contact has been deleted successfully.",
      });

      await fetchContacts();
    } catch (err) {
      console.error('Error deleting contact:', err);
      toast({
        title: "Error",
        description: "Failed to delete contact. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchContacts();

    // Use centralized subscription service for contacts updates
    centralizedSubscriptionService.setQueryClient(queryClient)
    const subscriptionKey = companyId ? `contacts-company-${companyId}` : clientId ? `contacts-client-${clientId}` : 'contacts-all'

    centralizedSubscriptionService.subscribe(subscriptionKey, `useContacts-${companyId || clientId || 'all'}`, {
      table: 'contacts',
      queryKeys: ['contacts'],
      callback: () => {
        console.log('Contacts realtime update');
        fetchContacts();
      }
    })

    return () => {
      centralizedSubscriptionService.unsubscribe(subscriptionKey, `useContacts-${companyId || clientId || 'all'}`)
    };
  }, [companyId, clientId, page, pageSize, search, queryClient]);

  return {
    contacts,
    totalCount,
    loading,
    error,
    createContact,
    updateContact,
    deleteContact,
    refetch: fetchContacts,
  };
};